import {useContext, useMemo, useState} from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import {
  Button,
  Caption,
  DataTable,
  IconButton,
  Paragraph,
  Title,
} from 'react-native-paper';
import {useMutation, useQueryClient} from 'react-query';
import firestore from '@react-native-firebase/firestore';

import {UserContext} from '../App';
import {useAuthPersonInfo, usePeople} from '../hooks/usePersonInfo';
import {
  filterLimitedPartners,
  Fund,
  injectMembers,
  LimitedPartner,
  Member,
  useFetchLimitedPartners,
  useFetchMembers,
  useMembers,
} from '../hooks/useDomainData';
import {MemberComponent} from './Members';
import {useAppSettings} from '../components/AppSettings';
import {LimitedPartnerEditor} from '../components/FormikStoreEditors/LimitedPartnerEditor';

export function LimitedPartnerComponent(props: {
  limitedPartner: LimitedPartner;
  fund?: Fund;
  readOnly?: boolean;
}): JSX.Element {
  const members = useMembers();
  const person = useAuthPersonInfo();
  const appSettings = useAppSettings();
  const queryClient = useQueryClient();

  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [currentFirestoreObject, setCurrentFirestoreObject] = useState<
    LimitedPartner | undefined
  >();

  const collectionName = 'limitedPartners';
  const handleDeleteFirestoreObject = useMutation(
    (firestoreObject: LimitedPartner) => {
      console.log('should delete: ' + firestoreObject.ref.path);
      return firestore()
        .collection(collectionName)
        .doc(firestoreObject.ref.id)
        .delete();
    },
    {
      onSettled: () => {
        console.log('deleting succeeded');
        return queryClient.invalidateQueries(collectionName);
      },
    },
  );

  const handleEditFirestoreObject = (firestoreObject?: LimitedPartner) => {
    console.log('open for edit or new object: ' + firestoreObject?.ref.path);
    setCurrentFirestoreObject(firestoreObject);
    setEditDialogVisible(true);
  };

  const themedStyles = StyleSheet.create({
    limitedPartnerContainer: {
      marginTop: 5,
      paddingTop: 5,
      borderTopWidth: 1,
      borderColor: appSettings.currentTheme.colors.primary,
    },
  });

  return (
    <View
      key={props.limitedPartner.limitedPartnerId}
      style={themedStyles.limitedPartnerContainer}>
      <View style={styles.horizontalSpaced}>
        <Paragraph>
          {`${
            props.fund !== undefined ? props.fund.fundCode + ' ' : ''
          }Limited Partner: ${
            props.limitedPartner.limitedPartnerName
          } (Partner Number #${props.limitedPartner.taxIdNumber})`}
        </Paragraph>
        <View style={styles.horizontalEnd}>
          {!props.readOnly && (person?.isAdmin || person?.isGuestRelations) && (
            <>
              <Paragraph>Limited Partner Actions: </Paragraph>
              <IconButton
                icon="pencil"
                size={18}
                color={appSettings.currentTheme.colors.primary}
                onPress={() => handleEditFirestoreObject(props.limitedPartner)}
              />
              <LimitedPartnerEditor
                dialogVisible={editDialogVisible}
                setDialogVisible={(visible: boolean) => {
                  setEditDialogVisible(visible);
                  if (!visible) {
                    setCurrentFirestoreObject(undefined);
                  }
                }}
                limitedPartner={currentFirestoreObject}
                allMembers={Array.from(members ?? [])}
              />
            </>
          )}
          {!props.readOnly && person?.isAdmin && (
            <IconButton
              icon="delete"
              size={18}
              color={appSettings.currentTheme.colors.primary}
              onPress={() =>
                handleDeleteFirestoreObject.mutate(props.limitedPartner)
              }
            />
          )}
        </View>
      </View>
      <View style={styles.indentOnce}>
        <LimitedPartnerMembers
          members={props.limitedPartner.members}
          readOnly={props.readOnly}
        />
      </View>
    </View>
  );
}

export function LimitedPartnerMembers(props: {
  members?: Member[];
  readOnly?: boolean;
}): JSX.Element {
  // console.log('members is ' + JSON.stringify(props.members));
  return (
    <View>
      {!props.members && <Caption>loading members...</Caption>}
      {props.members && props.members.length === 0 && (
        <Caption>No members.</Caption>
      )}
      {props.members &&
        props.members.map(member => (
          <MemberComponent
            key={member.memberId}
            member={member}
            readOnly={props.readOnly}
          />
        ))}
    </View>
  );
}

function LimitedPartners() {
  const user = useContext(UserContext);
  const people = usePeople();
  const members = useFetchMembers();
  let limitedPartners = useFetchLimitedPartners();
  const person = useAuthPersonInfo();
  const appSettings = useAppSettings();
  const queryClient = useQueryClient();

  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [currentFirestoreObject, setCurrentFirestoreObject] = useState<
    LimitedPartner | undefined
  >();

  const collectionName = 'limitedPartners';
  const handleDeleteFirestoreObject = useMutation(
    (firestoreObject: LimitedPartner) => {
      console.log('should delete: ' + firestoreObject.ref.path);
      return firestore()
        .collection(collectionName)
        .doc(firestoreObject.ref.id)
        .delete();
    },
    {
      onSettled: () => {
        console.log('deleting succeeded');
        return queryClient.invalidateQueries(collectionName);
      },
    },
  );
  const handleEditFirestoreObject = (firestoreObject?: LimitedPartner) => {
    console.log('open for edit or new object: ' + firestoreObject?.ref.path);
    setCurrentFirestoreObject(firestoreObject);
    setEditDialogVisible(true);
  };

  limitedPartners = useMemo(() => {
    if (
      limitedPartners !== undefined &&
      people !== undefined &&
      members !== undefined
    ) {
      injectMembers(limitedPartners, members, people);
    }
    return limitedPartners;
  }, [limitedPartners, members, people]);

  if (!user) {
    return null;
  }

  if (!person?.isAdmin && !person?.isGuestRelations) {
    limitedPartners = filterLimitedPartners(limitedPartners);
  }

  return (
    <>
      <View style={styles.horizontalCentered}>
        <Title style={styles.title}>Limited Partners</Title>
        {(person?.isAdmin || person?.isGuestRelations) && (
          <Button mode="outlined" onPress={() => handleEditFirestoreObject()}>
            Create Limited Partner
          </Button>
        )}
      </View>
      <ScrollView
        persistentScrollbar={true}
        style={[styles.container, styles.content]}>
        <DataTable>
          {!limitedPartners && (
            <DataTable.Row>
              <DataTable.Header>
                <DataTable.Title>loading...</DataTable.Title>
              </DataTable.Header>
            </DataTable.Row>
          )}
          {limitedPartners && (
            <DataTable.Header style={styles.shortHeader}>
              <DataTable.Title style={styles.center}>ID</DataTable.Title>
              <DataTable.Title style={styles.widerCell}>
                Limited Partner Name
              </DataTable.Title>
              <DataTable.Title style={styles.widerCell}>
                Linked Members
              </DataTable.Title>
              <DataTable.Title style={styles.center}>Actions</DataTable.Title>
            </DataTable.Header>
          )}
          {limitedPartners && limitedPartners.length === 0 && (
            <DataTable.Row>
              <DataTable.Cell>No limited partners found.</DataTable.Cell>
            </DataTable.Row>
          )}
          {limitedPartners &&
            limitedPartners.length > 0 &&
            limitedPartners.map(lp => (
              <DataTable.Row key={lp.limitedPartnerId}>
                <DataTable.Cell>{lp.limitedPartnerId}</DataTable.Cell>
                <DataTable.Cell style={styles.widerCell}>
                  {lp.limitedPartnerName}
                </DataTable.Cell>
                <DataTable.Cell style={styles.widerCell}>
                  {lp.members &&
                    lp.members.map(member => `${member.memberName}`).join(', ')}
                </DataTable.Cell>
                <DataTable.Cell>
                  {(person?.isAdmin || person?.isGuestRelations) && (
                    <>
                      <IconButton
                        icon="pencil"
                        size={18}
                        color={appSettings.currentTheme.colors.primary}
                        onPress={() => handleEditFirestoreObject(lp)}
                      />
                    </>
                  )}
                  {person?.isAdmin && (
                    <IconButton
                      icon="delete"
                      size={18}
                      color={appSettings.currentTheme.colors.primary}
                      onPress={() => handleDeleteFirestoreObject.mutate(lp)}
                    />
                  )}
                </DataTable.Cell>
              </DataTable.Row>
            ))}
        </DataTable>
      </ScrollView>
      <LimitedPartnerEditor
        dialogVisible={editDialogVisible}
        setDialogVisible={(visible: boolean) => {
          setEditDialogVisible(visible);
          if (!visible) {
            setCurrentFirestoreObject(undefined);
          }
        }}
        limitedPartner={currentFirestoreObject}
        allMembers={Array.from(members ?? [])}
      />
    </>
  );
}

const styles = StyleSheet.create({
  indentOnce: {paddingStart: 20, flexDirection: 'row'},
  indentTwice: {paddingStart: 40, flexDirection: 'row'},
  borderBottom: {borderBottomWidth: 1, borderColor: 'black'},
  widerCell: {
    minWidth: 150,
    width: 130,
    justifyContent: 'center',
    alignItems: 'center',
  },
  borderCell: {
    borderStartWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    flex: 1,
    position: 'relative',
  },
  content: {
    paddingHorizontal: 20,
  },
  center: {
    alignItems: 'center',
  },
  shortHeader: {
    height: 25,
  },
  title: {
    padding: 10,
    paddingHorizontal: 20,
  },
  horizontalSpaced: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  horizontalEnd: {
    flex: 1,
    flexDirection: 'row',
    paddingHorizontal: 10,
    justifyContent: 'flex-end',
    alignSelf: 'flex-end',
    alignItems: 'center',
  },
  horizontalCentered: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignContent: 'center',
    paddingHorizontal: 10,
  },
});

export default LimitedPartners;
